<template>
    <div class="embedded-attach-resource">
        <!-- Header -->
        <div class="embedded-header mb-4">
            <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                    <v-icon left color="primary">mdi-paperclip</v-icon>
                    <h3 class="text-h6 mb-0">Attach Resources</h3>
                </div>
                <v-btn icon small @click="$emit('close')">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
        </div>
        
        <!-- Tabs -->
        <v-tabs v-model="activeTab" background-color="transparent" class="mb-4">
            <v-tab>
                <v-icon left>mdi-folder-multiple</v-icon>
                From Resources
            </v-tab>
            <v-tab>
                <v-icon left>mdi-upload</v-icon>
                Upload Files
            </v-tab>
            <v-tab>
                <v-icon left>mdi-link-variant</v-icon>
                Document Link
            </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
            <!-- Select from existing resources -->
            <v-tab-item>
                <div class="pa-4">
                    <!-- Search and Filter -->
                    <div class="mb-4">
                        <v-row>
                            <v-col cols="8">
                                <v-text-field
                                    v-model="searchTerm"
                                    label="Search resources"
                                    prepend-inner-icon="mdi-magnify"
                                    outlined
                                    dense
                                    clearable
                                    hide-details
                                ></v-text-field>
                                
                            </v-col>
                            <v-col cols="4">
                                <v-select
                                    v-model="selectedCategory"
                                    :items="resourceCategories"
                                    label="Category"
                                    outlined
                                    dense
                                    hide-details
                                ></v-select>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Selected Resources Count -->
                    <div v-if="selectedResources.length > 0" class="mb-4">
                        <v-chip color="primary" outlined>
                            <v-icon left small>mdi-check-circle</v-icon>
                            {{ selectedResources.length }} resource{{ selectedResources.length > 1 ? 's' : '' }} selected
                        </v-chip>
                    </div>

                    <!-- Resources List -->
                    <div class="resources-list" style="max-height: 300px; overflow-y: auto;">
                        <v-row>
                            <v-col 
                                v-for="resource in filteredResources" 
                                :key="resource.id" 
                                cols="12"
                            >
                                <v-card 
                                    outlined 
                                    hover 
                                    :class="{ 'selected-resource': isResourceSelected(resource.id) }"
                                    @click="toggleResourceSelection(resource)"
                                    class="resource-selection-card"
                                >
                                    <v-card-text class="pa-3">
                                        <div class="d-flex align-center">
                                            <v-checkbox
                                                :input-value="isResourceSelected(resource.id)"
                                                @change="toggleResourceSelection(resource)"
                                                color="primary"
                                                hide-details
                                                class="ma-0 mr-3"
                                            ></v-checkbox>
                                            <v-icon 
                                                :color="getFileTypeColor(resource.file_type)" 
                                                size="24" 
                                                class="mr-3"
                                            >
                                                {{ getFileTypeIcon(resource.file_type) }}
                                            </v-icon>
                                            <div class="flex-grow-1">
                                                <div class="font-weight-bold">{{ resource.name }}</div>
                                                <div class="text-caption grey--text">
                                                    {{ resource.description || 'No description' }}
                                                </div>
                                                <div class="d-flex align-center mt-1">
                                                    <v-chip 
                                                        v-if="resource.category"
                                                        :color="getCategoryColor(resource.category)" 
                                                        x-small 
                                                        text-color="white"
                                                        class="mr-2"
                                                    >
                                                        {{ resource.category }}
                                                    </v-chip>
                                                    <span v-if="resource.size" class="text-caption grey--text">
                                                        {{ formatFileSize(resource.size) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- No resources found -->
                        <div v-if="filteredResources.length === 0" class="text-center py-8">
                            <v-icon size="64" color="grey lighten-2">mdi-folder-open-outline</v-icon>
                            <h3 class="grey--text mt-4">No resources found</h3>
                            <p class="grey--text">Try adjusting your search criteria</p>
                        </div>
                    </div>
                </div>
            </v-tab-item>

            <!-- Upload new files -->
            <v-tab-item>
                <div class="pa-4">
                    <v-file-input
                        v-model="selectedFiles"
                        label="Select files to upload"
                        prepend-icon="mdi-paperclip"
                        multiple
                        show-size
                        chips
                        clearable
                        outlined
                        @change="handleFileSelection"
                    >
                        <template v-slot:selection="{ text }">
                            <v-chip
                                color="primary"
                                small
                                label
                                class="ma-1"
                            >
                                {{ text }}
                            </v-chip>
                        </template>
                    </v-file-input>

                    <!-- Upload Progress -->
                    <div v-if="uploadingFiles.length > 0" class="mt-4">
                        <h4 class="mb-2">Uploading Files...</h4>
                        <div v-for="upload in uploadingFiles" :key="upload.name" class="mb-2">
                            <div class="d-flex align-center">
                                <v-icon small class="mr-2">{{ getFileTypeIcon(upload.type) }}</v-icon>
                                <span class="flex-grow-1">{{ upload.name }}</span>
                                <v-progress-circular
                                    v-if="upload.uploading"
                                    indeterminate
                                    size="20"
                                    width="2"
                                    color="primary"
                                ></v-progress-circular>
                                <v-icon v-else-if="upload.success" color="success" small>mdi-check</v-icon>
                                <v-icon v-else-if="upload.error" color="error" small>mdi-alert</v-icon>
                            </div>
                            <v-progress-linear
                                v-if="upload.uploading"
                                indeterminate
                                color="primary"
                                height="2"
                            ></v-progress-linear>
                        </div>
                    </div>

                    <!-- Uploaded Files -->
                    <div v-if="uploadedResources.length > 0" class="mt-4">
                        <h4 class="mb-2">Uploaded Files:</h4>
                        <v-chip
                            v-for="resource in uploadedResources"
                            :key="resource.id"
                            color="success"
                            outlined
                            class="ma-1"
                            close
                            @click:close="removeUploadedResource(resource.id)"
                        >
                            <v-icon left small>{{ getFileTypeIcon(resource.file_type) }}</v-icon>
                            {{ resource.name }}
                        </v-chip>
                    </div>
                </div>
            </v-tab-item>

            <!-- Add Document Link -->
            <v-tab-item>
                <div class="pa-4">
                    <v-form ref="linkForm" v-model="linkFormValid">
                        <v-text-field
                            v-model="documentLink.url"
                            label="Document URL *"
                            placeholder="https://example.com/document.pdf"
                            prepend-inner-icon="mdi-link"
                            outlined
                            dense
                            :rules="urlRules"
                            hint="Paste the link to your document (Google Docs, Dropbox, OneDrive, etc.)"
                            persistent-hint
                            class="mb-4"
                        ></v-text-field>

                        <v-text-field
                            v-model="documentLink.name"
                            label="Document Name *"
                            placeholder="e.g., Project Proposal 2025"
                            prepend-inner-icon="mdi-text"
                            outlined
                            dense
                            :rules="nameRules"
                            class="mb-4"
                        ></v-text-field>

                        <v-textarea
                            v-model="documentLink.description"
                            label="Description (Optional)"
                            placeholder="Add a brief description of this document"
                            prepend-inner-icon="mdi-text-box-outline"
                            outlined
                            dense
                            rows="3"
                            auto-grow
                            class="mb-4"
                        ></v-textarea>

                        <v-btn
                            @click="addDocumentLink"
                            color="primary"
                            :disabled="!linkFormValid || !documentLink.url || !documentLink.name"
                            :loading="addingLink"
                            block
                        >
                            <v-icon left>mdi-plus</v-icon>
                            Add Document Link
                        </v-btn>
                    </v-form>

                    <!-- Added Links Preview -->
                    <div v-if="addedLinks.length > 0" class="mt-4">
                        <h4 class="mb-2">Added Links:</h4>
                        <v-card
                            v-for="link in addedLinks"
                            :key="link.tempId"
                            outlined
                            class="mb-2"
                        >
                            <v-card-text class="pa-3">
                                <div class="d-flex align-center">
                                    <v-icon color="blue" class="mr-3">mdi-link-variant</v-icon>
                                    <div class="flex-grow-1">
                                        <div class="font-weight-bold">{{ link.name }}</div>
                                        <div class="text-caption grey--text text-truncate">{{ link.url }}</div>
                                        <div v-if="link.description" class="text-caption mt-1">{{ link.description }}</div>
                                    </div>
                                    <v-btn
                                        icon
                                        small
                                        color="error"
                                        @click="removeAddedLink(link.tempId)"
                                    >
                                        <v-icon small>mdi-close</v-icon>
                                    </v-btn>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                </div>
            </v-tab-item>
        </v-tabs-items>
        
        <!-- Actions -->
        <div class="embedded-actions mt-4">
            <div class="d-flex justify-space-between align-center">
                <div class="text-caption grey--text">
                    {{ totalSelectedCount }} resource{{ totalSelectedCount !== 1 ? 's' : '' }} ready to attach
                </div>
                <div>
                    <v-btn @click="$emit('close')" text class="mr-2">
                        Cancel
                    </v-btn>
                    <v-btn 
                        @click="attachResources" 
                        color="primary" 
                        :loading="loading"
                        :disabled="totalSelectedCount === 0"
                    >
                        <v-icon left>mdi-paperclip</v-icon>
                        Attach {{ totalSelectedCount }} Resource{{ totalSelectedCount !== 1 ? 's' : '' }}
                    </v-btn>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AttachResourceDialogEmbedded',
    inject: ['resources', 'companies', 'addResource'],
    props: {
        defaultCompanyId: {
            type: [Number, String],
            default: null
        },
        alreadyAttached: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loading: false,
            activeTab: 0,
            searchTerm: '',
            selectedCategory: 'All',
            selectedResources: [],
            selectedFiles: [],
            uploadedResources: [],
            uploadingFiles: [],
            documentLink: {
                url: '',
                name: '',
                description: ''
            },
            addedLinks: [],
            linkFormValid: false,
            addingLink: false,
            urlRules: [
                v => !!v || 'URL is required',
                v => /^https?:\/\/.+/.test(v) || 'Must be a valid URL starting with http:// or https://'
            ],
            nameRules: [
                v => !!v || 'Name is required',
                v => (v && v.length >= 3) || 'Name must be at least 3 characters'
            ]
        };
    },
    computed: {
        availableResources() {
            const resources = this.resources() || [];
            return resources.filter(resource => 
                !this.alreadyAttached.some(attached => 
                    attached.resource_id === resource.id || attached.id === resource.id
                )
            );
        },
        filteredResources() {
            let filtered = this.availableResources;

            if (this.searchTerm) {
                const search = this.searchTerm.toLowerCase();
                filtered = filtered.filter(resource =>
                    resource.name.toLowerCase().includes(search) ||
                    resource.description?.toLowerCase().includes(search) ||
                    resource.category?.toLowerCase().includes(search)
                );
            }

            if (this.selectedCategory && this.selectedCategory !== 'All') {
                filtered = filtered.filter(resource => resource.category === this.selectedCategory);
            }

            return filtered;
        },
        resourceCategories() {
            const categories = ['All'];
            const resources = this.availableResources;
            
            resources.forEach(resource => {
                if (resource.category && !categories.includes(resource.category)) {
                    categories.push(resource.category);
                }
            });

            return categories;
        },
        totalSelectedCount() {
            return this.selectedResources.length + this.uploadedResources.length + this.addedLinks.length;
        }
    },
    methods: {
        isResourceSelected(resourceId) {
            return this.selectedResources.some(r => r.id === resourceId);
        },
        toggleResourceSelection(resource) {
            const index = this.selectedResources.findIndex(r => r.id === resource.id);
            if (index > -1) {
                this.selectedResources.splice(index, 1);
            } else {
                this.selectedResources.push(resource);
            }
        },
        async handleFileSelection(files) {
            if (!files || files.length === 0) return;

            for (const file of files) {
                await this.uploadFile(file);
            }
            
            // Clear the file input
            this.selectedFiles = [];
        },
        async uploadFile(file) {
            const uploadInfo = {
                name: file.name,
                type: this.getFileTypeFromMime(file.type),
                uploading: true,
                success: false,
                error: false
            };
            
            this.uploadingFiles.push(uploadInfo);

            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('name', file.name);
                formData.append('file_type', this.getFileTypeFromMime(file.type));
                formData.append('size', file.size);
                formData.append('type', 'document');
                formData.append('company_id', this.defaultCompanyId || '');

                const response = await this.$axios.post('/api/resources', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.data.success || response.data.response) {
                    const newResource = response.data.data || response.data;
                    
                    // Add to global resources list
                    this.addResource(newResource);
                    
                    // Add to uploaded resources for attachment
                    this.uploadedResources.push(newResource);
                    
                    uploadInfo.uploading = false;
                    uploadInfo.success = true;
                    
                    this.$toast.success(`${file.name} uploaded successfully`);
                } else {
                    throw new Error(response.data.message || 'Upload failed');
                }
            } catch (error) {
                console.error('Error uploading file:', error);
                uploadInfo.uploading = false;
                uploadInfo.error = true;
                this.$toast.error(`Failed to upload ${file.name}`);
            }
        },
        removeUploadedResource(resourceId) {
            const index = this.uploadedResources.findIndex(r => r.id === resourceId);
            if (index > -1) {
                this.uploadedResources.splice(index, 1);
            }
        },
        async addDocumentLink() {
            if (!this.$refs.linkForm.validate()) {
                return;
            }

            this.addingLink = true;

            try {
                // Create resource entry for the link
                const payload = {
                    name: this.documentLink.name,
                    url: this.documentLink.url,
                    description: this.documentLink.description || '',
                    file_type: 'link',
                    type: 'link',
                    size: 0,
                    company_id: this.defaultCompanyId || null
                };

                const response = await this.$axios.post('/api/resources', payload);

                if (response.data.success || response.data.response) {
                    const newResource = response.data.data || response.data;
                    
                    // Add to global resources list
                    this.addResource(newResource);
                    
                    // Add to added links with temp ID for tracking
                    const linkWithTempId = {
                        ...newResource,
                        tempId: Date.now() + Math.random()
                    };
                    this.addedLinks.push(linkWithTempId);
                    
                    this.$toast.success('Document link added successfully');
                    
                    // Reset form
                    this.documentLink = {
                        url: '',
                        name: '',
                        description: ''
                    };
                    this.$refs.linkForm.resetValidation();
                } else {
                    throw new Error(response.data.message || 'Failed to add link');
                }
            } catch (error) {
                console.error('Error adding document link:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to add document link');
            } finally {
                this.addingLink = false;
            }
        },
        removeAddedLink(tempId) {
            const index = this.addedLinks.findIndex(l => l.tempId === tempId);
            if (index > -1) {
                this.addedLinks.splice(index, 1);
            }
        },
        attachResources() {
            const allSelectedResources = [
                ...this.selectedResources, 
                ...this.uploadedResources,
                ...this.addedLinks
            ];
            
            if (allSelectedResources.length === 0) {
                this.$toast.warning('Please select at least one resource to attach');
                return;
            }

            const attachments = allSelectedResources.map(resource => ({
                id: resource.id,
                name: resource.name,
                url: resource.url,
                file_type: resource.file_type || 'link',
                size: resource.size,
                category: resource.category,
                description: resource.description
            }));

            this.$emit('resources-attached', attachments);
            this.$emit('close');
        },
        getFileTypeFromMime(mimeType) {
            const mimeMap = {
                'application/pdf': 'pdf',
                'application/msword': 'doc',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
                'application/vnd.ms-excel': 'xls',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
                'application/vnd.ms-powerpoint': 'ppt',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
                'text/plain': 'txt',
                'application/zip': 'zip',
                'image/jpeg': 'image',
                'image/png': 'image',
                'image/gif': 'image',
                'video/mp4': 'video',
                'audio/mpeg': 'audio'
            };
            return mimeMap[mimeType] || 'document';
        },
        getFileTypeIcon(fileType) {
            const iconMap = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word-box',
                docx: 'mdi-file-word-box',
                xls: 'mdi-file-excel-box',
                xlsx: 'mdi-file-excel-box',
                ppt: 'mdi-file-powerpoint-box',
                pptx: 'mdi-file-powerpoint-box',
                txt: 'mdi-file-document-outline',
                zip: 'mdi-folder-zip',
                image: 'mdi-file-image-box',
                video: 'mdi-file-video-box',
                audio: 'mdi-file-music-box',
                link: 'mdi-link-variant',
                document: 'mdi-file-document-outline'
            };
            return iconMap[fileType] || 'mdi-file-document-outline';
        },
        getFileTypeColor(fileType) {
            const colorMap = {
                pdf: 'red',
                doc: 'blue',
                docx: 'blue',
                xls: 'green',
                xlsx: 'green',
                ppt: 'orange',
                pptx: 'orange',
                txt: 'grey',
                zip: 'purple',
                image: 'pink',
                video: 'deep-purple',
                audio: 'teal',
                link: 'blue'
            };
            return colorMap[fileType] || 'grey';
        },
        getCategoryColor(category) {
            const colorMap = {
                'SOP': 'blue',
                'Template': 'green',
                'EOD': 'orange',
                'SOD': 'purple',
                'Guide': 'teal',
                'Other': 'grey'
            };
            return colorMap[category] || 'primary';
        },
        formatFileSize(bytes) {
            if (!bytes) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        }
    }
};
</script>

<style scoped>
.embedded-attach-resource {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
    background: #fafafa;
}

.embedded-header h3 {
    color: #333;
}

.embedded-actions {
    border-top: 1px solid #e0e0e0;
    padding-top: 16px;
}

.resource-selection-card {
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
}

.resource-selection-card:hover {
    border-color: #e0e0e0;
    transform: translateY(-1px);
}

.selected-resource {
    border-color: #1976d2 !important;
    background-color: #e3f2fd;
}

.resources-list {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    background-color: white;
}

:deep(.v-tabs-bar) {
    box-shadow: none;
    border-bottom: 1px solid #e0e0e0;
}

:deep(.v-tab) {
    text-transform: none;
    font-weight: 500;
}

:deep(.v-tab--active) {
    color: #1976d2;
}

:deep(.v-file-input .v-file-input__text) {
    height: auto;
}
</style>